import React from 'react';
import { TickerDisplayData } from '../types/ticker';
import { UpArrowIcon, DownArrowIcon } from '../components/Icons';

export interface UseAnalystViewsTabStatisticsOptions {
  // Optional syncing state handling (for ListAnalystViews)
  syncingTickers?: Set<string>;
  unsyncedTickers?: Set<string>;
  pollingLimitReached?: Set<string>;
}

/**
 * Get arrow direction and color for EPS comparisons
 */
export const getAnalystViewsEpsArrowInfo = (
  statisticKey: string,
  ticker: TickerDisplayData
): { direction: 'up' | 'down' | null, isGood: boolean } => {
  const currentValue = ticker[statisticKey as keyof typeof ticker] as number | undefined;
  let yearAgoValue: number | undefined;

  // Get the corresponding year-ago value
  switch (statisticKey) {
    case 'thisQuarterEpsAvg':
      yearAgoValue = (ticker as any).thisQuarterEpsYearAgo;
      break;
    case 'nextQuarterEpsAvg':
      yearAgoValue = (ticker as any).nextQuarterEpsYearAgo;
      break;
    case 'thisYearEpsAvg':
      yearAgoValue = (ticker as any).thisYearEpsYearAgo;
      break;
    default:
      return { direction: null, isGood: false };
  }

  if (currentValue === undefined || currentValue === null || 
      yearAgoValue === undefined || yearAgoValue === null) {
    return { direction: null, isGood: false };
  }

  // For EPS, higher is better
  if (currentValue > yearAgoValue) {
    return { direction: 'up', isGood: true };
  } else if (currentValue < yearAgoValue) {
    return { direction: 'down', isGood: false };
  }

  return { direction: null, isGood: false };
};

/**
 * Get arrow tooltip text for EPS comparisons
 */
export const getAnalystViewsEpsArrowTooltip = (
  statisticKey: string,
  ticker: TickerDisplayData
): string => {
  const currentValue = ticker[statisticKey as keyof typeof ticker] as number | undefined;
  let yearAgoValue: number | undefined;

  switch (statisticKey) {
    case 'thisQuarterEpsAvg':
      yearAgoValue = (ticker as any).thisQuarterEpsYearAgo;
      break;
    case 'nextQuarterEpsAvg':
      yearAgoValue = (ticker as any).nextQuarterEpsYearAgo;
      break;
    case 'thisYearEpsAvg':
      yearAgoValue = (ticker as any).thisYearEpsYearAgo;
      break;
    default:
      return '';
  }

  if (currentValue === undefined || currentValue === null || 
      yearAgoValue === undefined || yearAgoValue === null) {
    return '';
  }

  const change = currentValue - yearAgoValue;
  const changePercent = ((change / yearAgoValue) * 100).toFixed(1);
  
  return `Compared to $${yearAgoValue.toFixed(2)} one year ago (${change > 0 ? '+' : ''}${changePercent}%)`;
};

/**
 * Format statistic value for Analyst Views tab
 */
export const formatAnalystViewsStatisticValue = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseAnalystViewsTabStatisticsOptions
): React.ReactNode => {
  const { syncingTickers, unsyncedTickers, pollingLimitReached } = options;
  
  const symbol = ticker.ticker;
  const value = ticker[statisticKey as keyof typeof ticker];
  
  // Handle syncing/loading states (for ListAnalystViews)
  if (syncingTickers || unsyncedTickers || pollingLimitReached) {
    const isSyncing = syncingTickers?.has(symbol);
    const isUnsynced = unsyncedTickers?.has(symbol);
    const isPollingLimitReached = pollingLimitReached?.has(symbol);
    
    // Show - if polling limit reached for unsynced tickers
    if (isPollingLimitReached) {
      return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    }
    
    // Show loading indicator for syncing or unsynced tickers
    if (isSyncing || isUnsynced || ticker.isSyncing) {
      return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
    }
  }
  
  // If analyst views are unavailable (e.g., ETF/ETV not present in API), show N/A
  if ((ticker as any).isAnalystViewsUnavailable) {
    return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  }
  
  if (value === undefined || value === null) return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  
  // Handle date formatting for nextEarningsDate
  if (statisticKey === 'nextEarningsDate') {
    let earningsDate: Date | null = null;
    
    if (value instanceof Date) {
      earningsDate = value;
    } else if (typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        earningsDate = date;
      }
    }
    
    if (earningsDate) {
      // Get today's date in New York time
      const now = new Date();
      const nyTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const todayNY = new Date(nyTime.getFullYear(), nyTime.getMonth(), nyTime.getDate());
      
      // Get the earnings date at midnight in New York time for comparison
      const earningsDateNY = new Date(earningsDate.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const earningsDateOnly = new Date(earningsDateNY.getFullYear(), earningsDateNY.getMonth(), earningsDateNY.getDate());
      
      // If earnings date is before today in NY time, show "TBD"
      if (earningsDateOnly < todayNY) {
        return 'TBD';
      }
      
      return earningsDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  }
  
  // Check if this is an EPS statistic that should have arrows
  const epsStatistics = ['thisQuarterEpsAvg', 'nextQuarterEpsAvg', 'thisYearEpsAvg'];
  if (epsStatistics.includes(statisticKey) && typeof value === 'number') {
    const arrowInfo = getAnalystViewsEpsArrowInfo(statisticKey, ticker);
    const tooltipText = getAnalystViewsEpsArrowTooltip(statisticKey, ticker);
    const formattedValue = `$${value.toFixed(2)}`;
    
    return (
      <span className="inline-flex items-center gap-1">
        {formattedValue}
        {arrowInfo.direction === 'up' && (
          <div className="relative group/arrow">
            <UpArrowIcon 
              className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
            />
            {tooltipText && (
              <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                {tooltipText}
              </div>
            )}
          </div>
        )}
        {arrowInfo.direction === 'down' && (
          <div className="relative group/arrow">
            <DownArrowIcon 
              className={`w-3 h-3 ${arrowInfo.isGood ? 'text-green-light dark:text-green-500' : 'text-red-light dark:text-red-500'}`} 
            />
            {tooltipText && (
              <div className="absolute hidden group-hover/arrow:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                {tooltipText}
              </div>
            )}
          </div>
        )}
      </span>
    );
  }
  
  if (typeof value === 'number') {
    if (statisticKey.includes('Percent') || statisticKey === 'priceTargetDifference') {
      return `${value.toFixed(1)}%`;
    }
    if (statisticKey.includes('Price') || statisticKey.includes('Eps') || statisticKey === 'lastClosingPrice') {
      return `$${value.toFixed(2)}`;
    }
    return value.toString();
  }
  
  return value.toString();
};

/**
 * Get statistic color for Analyst Views tab
 */
export const getAnalystViewsStatisticColor = (
  statisticKey: string,
  ticker: TickerDisplayData,
  options: UseAnalystViewsTabStatisticsOptions
): string => {
  const value = ticker[statisticKey as keyof typeof ticker];
  
  // Color coding for average recommendation
  if (statisticKey === 'averageRecommendation' && typeof value === 'string') {
    if (value.includes('Strong Buy') || value.includes('Buy')) {
      return 'text-green-light dark:text-green-500';
    } else if (value.includes('Strong Sell') || value.includes('Sell')) {
      return 'text-red-light dark:text-red-500';
    } else if (value.includes('Hold')) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
  }
  
  // Color coding for price target difference
  if (statisticKey === 'priceTargetDifference' && typeof value === 'number') {
    if (value > 0) {
      return 'text-green-light dark:text-green-500'; // Positive difference (price target above current price)
    } else if (value < 0) {
      return 'text-red-light dark:text-red-500'; // Negative difference (price target below current price)
    }
  }
  
  // Color coding for price change
  if (statisticKey === 'lastClosingPrice' && typeof value === 'number') {
    const change = ticker.change;
    if (change > 0) {
      return 'text-green-light dark:text-green-500';
    } else if (change < 0) {
      return 'text-red-light dark:text-red-500';
    }
  }
  
  // Default color for other statistics
  return 'text-text-light-neutral dark:text-text-secondary';
};

